import React, { useState, useEffect } from 'react';
import { Flame, Trophy, Calendar, Star, Award, TrendingUp } from 'lucide-react';
import { Card } from '../components/ui/card';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MILESTONE_LABELS = {
  3: { label: '3-Day Spark', icon: Flame, color: 'text-orange-400' },
  7: { label: 'Weekly Warrior', icon: Star, color: 'text-yellow-500' },
  14: { label: 'Fortnight Focus', icon: TrendingUp, color: 'text-blue-500' },
  21: { label: '3-Week Scholar', icon: Award, color: 'text-purple-500' },
  30: { label: 'Monthly Master', icon: Trophy, color: 'text-amber-600' },
  60: { label: 'Dedicated Disciple', icon: Trophy, color: 'text-emerald-500' },
  90: { label: 'Legendary Reader', icon: Trophy, color: 'text-rose-500' },
};

const ReadingStreaks = () => {
  const { user, isAuthenticated } = useAuth();
  const [streak, setStreak] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated()) fetchStreak();
    else setLoading(false);
  }, []);

  const fetchStreak = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${API_URL}/api/reading-streaks`, { headers, credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setStreak(data.streak);
      }
    } catch (err) {
      console.error('Failed to load streak:', err);
    } finally {
      setLoading(false);
    }
  };

  const renderCalendar = () => {
    if (!streak?.reading_log?.length) return null;

    const logSet = new Set(streak.reading_log);
    const today = new Date();
    const days = [];

    // Show last 35 days (5 weeks)
    for (let i = 34; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      const dateStr = d.toISOString().split('T')[0];
      const isRead = logSet.has(dateStr);
      const isToday = i === 0;

      days.push(
        <div
          key={dateStr}
          data-testid={`calendar-day-${dateStr}`}
          title={`${dateStr}${isRead ? ' - Read' : ''}`}
          className={`w-8 h-8 rounded-md flex items-center justify-center text-xs font-medium transition-all ${
            isRead
              ? 'bg-amber-500 text-white shadow-sm'
              : isToday
                ? 'bg-gray-200 text-gray-600 ring-2 ring-amber-400'
                : 'bg-gray-100 text-gray-400'
          }`}
        >
          {d.getDate()}
        </div>
      );
    }

    return (
      <div className="grid grid-cols-7 gap-1.5" data-testid="streak-calendar">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
          <div key={i} className="w-8 h-6 flex items-center justify-center text-xs font-semibold text-gray-400">
            {d}
          </div>
        ))}
        {days}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-amber-50">
        <Header />
        <div className="container mx-auto px-4 py-16 text-center">Loading...</div>
        <Footer />
      </div>
    );
  }

  if (!isAuthenticated()) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-amber-50">
        <Header />
        <div className="container mx-auto px-4 py-16 text-center">
          <Flame className="h-16 w-16 text-amber-500 mx-auto mb-4" />
          <h1 className="text-3xl font-bold mb-2">Reading Streaks</h1>
          <p className="text-gray-600 mb-6">Sign in to track your daily reading and earn bonus rewards.</p>
          <a href="/login" className="inline-block px-6 py-3 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700">
            Sign In to Start
          </a>
        </div>
        <Footer />
      </div>
    );
  }

  const currentStreak = streak?.current_streak || 0;
  const longestStreak = streak?.longest_streak || 0;
  const totalDays = streak?.total_days_read || 0;
  const bonusDiscount = streak?.bonus_discount_pct || 0;
  const milestones = streak?.streak_milestones || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-amber-50">
      <Header />
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Hero */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-amber-100 rounded-full text-amber-800 text-sm font-semibold mb-4">
            <Flame className="h-4 w-4" /> Daily Reading Challenge
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2" data-testid="streak-title">Reading Streaks</h1>
          <p className="text-gray-600">Read daily to build your streak and unlock bonus discounts</p>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="p-4 text-center bg-gradient-to-br from-amber-500 to-orange-500 text-white" data-testid="current-streak-card">
            <Flame className="h-8 w-8 mx-auto mb-1" />
            <div className="text-3xl font-bold">{currentStreak}</div>
            <div className="text-xs opacity-90">Current Streak</div>
          </Card>
          <Card className="p-4 text-center" data-testid="longest-streak-card">
            <Trophy className="h-8 w-8 mx-auto mb-1 text-amber-500" />
            <div className="text-3xl font-bold text-gray-900">{longestStreak}</div>
            <div className="text-xs text-gray-500">Best Streak</div>
          </Card>
          <Card className="p-4 text-center" data-testid="total-days-card">
            <Calendar className="h-8 w-8 mx-auto mb-1 text-blue-500" />
            <div className="text-3xl font-bold text-gray-900">{totalDays}</div>
            <div className="text-xs text-gray-500">Days Read</div>
          </Card>
          <Card className="p-4 text-center bg-gradient-to-br from-emerald-500 to-teal-500 text-white" data-testid="bonus-discount-card">
            <Star className="h-8 w-8 mx-auto mb-1" />
            <div className="text-3xl font-bold">{bonusDiscount}%</div>
            <div className="text-xs opacity-90">Streak Bonus</div>
          </Card>
        </div>

        {/* Calendar */}
        <Card className="p-6 mb-8" data-testid="streak-calendar-section">
          <h2 className="font-bold text-lg mb-4 flex items-center gap-2">
            <Calendar className="h-5 w-5 text-amber-500" />
            Last 5 Weeks
          </h2>
          {renderCalendar()}
          <div className="flex items-center gap-4 mt-4 text-xs text-gray-500">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-amber-500" /> Read
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-gray-100" /> Not read
            </div>
          </div>
        </Card>

        {/* Milestones */}
        <Card className="p-6 mb-8" data-testid="milestones-section">
          <h2 className="font-bold text-lg mb-4 flex items-center gap-2">
            <Award className="h-5 w-5 text-purple-500" />
            Milestones
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {Object.entries(MILESTONE_LABELS).map(([threshold, { label, icon: Icon, color }]) => {
              const unlocked = milestones.includes(Number(threshold));
              return (
                <div
                  key={threshold}
                  data-testid={`milestone-${threshold}`}
                  className={`p-3 rounded-lg border text-center transition-all ${
                    unlocked
                      ? 'bg-white border-amber-300 shadow-sm'
                      : 'bg-gray-50 border-gray-200 opacity-50'
                  }`}
                >
                  <Icon className={`h-6 w-6 mx-auto mb-1 ${unlocked ? color : 'text-gray-300'}`} />
                  <div className="text-xs font-semibold">{label}</div>
                  <div className="text-[10px] text-gray-400">{threshold} days</div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* How It Works */}
        <Card className="p-6" data-testid="how-it-works">
          <h2 className="font-bold text-lg mb-4">How Streaks Work</h2>
          <div className="space-y-3 text-sm text-gray-600">
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold shrink-0">1</span>
              <p>Read any chapter or ebook content each day to log your streak.</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold shrink-0">2</span>
              <p>Every 3 consecutive days adds 1% bonus discount (up to 10%).</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold shrink-0">3</span>
              <p>Reach milestone thresholds to unlock achievement badges.</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold shrink-0">4</span>
              <p>Miss a day? Your current streak resets, but your best streak and total days are preserved.</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default ReadingStreaks;
